import {useMutation} from '@apollo/client';
import {
  SetAccountStarredDocument,
  useStarredAccountIdsQuery,
} from '@transactions/graphql';
import * as React from 'react';
import {setStarredAccountIds} from 'src/apolloCache/starredAccounts';

/**
 * The starred account ids, in pin order, and a toggle that updates the
 * cached list optimistically. Shared by the Accounts list and the account
 * page's menu, so both read and write the same cache entry.
 */
export const useStarredAccounts = () => {
  const {data, loading} = useStarredAccountIdsQuery();
  const [setStarred] = useMutation(SetAccountStarredDocument);
  const starredIds = React.useMemo(
    () => data?.starredAccountIds ?? [],
    [data],
  );

  const toggleStar = (accountId: string) => {
    const starred = !starredIds.includes(accountId);
    const without = starredIds.filter((id) => id !== accountId);
    const next = starred ? [...without, accountId] : without;
    void setStarred({
      variables: {accountId, starred},
      optimisticResponse: {setAccountStarred: next},
      update: (cache, result) => {
        const ids = result.data?.setAccountStarred;
        if (ids) {
          setStarredAccountIds(cache, ids);
        }
      },
    });
  };

  return {starredIds, loading, toggleStar};
};
